// Provenance: repo:tds-reconciliation-portal:frontend/src/components/KeepOnePanel.tsx
// (ADR-0005) — restyled (ADR-0010). The judgement rules carry whole: the batch is reviewed
// BEFORE the money moves; held-back groups render in full (they are held back precisely so a
// person reads them); the sample shows the batch's EDGES, not its head; and every applied
// group is reversible from the Cleared view.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Copy } from 'lucide-react'
import { Fragment, useState } from 'react'

import { ApiError, api, postJson } from '../api/client'
import { decidedChip, extraLinesNote } from '../api/keepOne'
import { weakVerdicts } from '../api/rawDupReview'
import { fmtMoney } from '../format'
import { InfoTip } from './InfoTip'
import { Btn } from './Modal'
import { Money } from './Money'
import { Chip, type Tone } from './badges'
import { showToast } from './toast'

const fmt = new Intl.NumberFormat('en-IN')

export interface DocLine {
  row_index: number
  date: string
  payor: string
  debit: string
  narration: string
  /** one of the identical copies (vs another line on the same document) */
  copy: boolean
}

export interface KeepOneGroup {
  payor: string
  date: string
  docno: string
  debit: string
  n: number
  row_indexes: number[]
  keep: number
  dispose: number[]
  extra_lines_on_doc: number
  strength: 'strong' | 'weak'
  why?: string
  /** held-back groups only: every line the file carries under this document
   *  number — the evidence the judgement is actually about */
  doc_lines?: DocLine[]
}

export interface KeepOnePlan {
  reviewable: boolean
  groups: number
  rows: number
  rule: string
  takeable: { groups: number; rows_disposed: number; value: string }
  recorded: {
    groups: number
    rows_disposed: number
    value: string
    awaiting_checker: number
  }
  held_back: {
    groups: number
    rows_disposed: number
    value: string
    reason: string
    items: KeepOneGroup[]
  }
  sample: KeepOneGroup[]
}

const th = 'py-1 pr-2 text-left text-xs font-semibold text-muted-foreground'
const td = 'py-1 pr-2 align-top'

function GroupTable({ rows, showWhy, judge, busy }: {
  rows: KeepOneGroup[]
  showWhy?: boolean
  judge?: (g: KeepOneGroup, decision: 'both_real' | 'keep_one') => void
  busy?: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            {showWhy && <th className={th}>why shown</th>}
            <th className={th}>Party</th>
            <th className={th}>Document</th>
            <th className={th}>Date</th>
            <th className={`${th} text-right`}>Each (₹)</th>
            <th className={`${th} text-right`}>Copies</th>
            <th className={`${th} text-right`}>Dropped (₹)</th>
            {judge && <th className={th}>Your judgement</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <Fragment key={`${g.docno}-${g.keep}`}>
              <tr className="border-t border-border/60">
                {showWhy && (
                  <td className={td}><Chip>{g.why}</Chip></td>
                )}
                <td className={td}>{g.payor}</td>
                <td className={`${td} font-mono`}>{g.docno || '—'}</td>
                <td className={td}>{(g.date ?? '').slice(0, 10)}</td>
                <td className={`${td} text-right`}><Money v={g.debit} className="text-xs" /></td>
                <td className={`${td} whitespace-nowrap text-right`}>
                  {g.n}
                  {g.extra_lines_on_doc > 0 && (
                    <span
                      className="text-orange-600"
                      title={`${g.extra_lines_on_doc} other line(s) on this document — shown underneath`}
                    >
                      {' '}{extraLinesNote(g.extra_lines_on_doc)}
                    </span>
                  )}
                </td>
                <td className={`${td} text-right`}>
                  <Money v={String(Number(g.debit) * (g.n - 1))} className="text-xs" />
                </td>
                {judge && (
                  <td className={td}>
                    <span className="flex gap-1">
                      <Btn size="xs" disabled={busy} onClick={() => judge(g, 'both_real')}>
                        Both real
                      </Btn>
                      <Btn size="xs" kind="warning" disabled={busy}
                        onClick={() => judge(g, 'keep_one')}>
                        Keep one, drop {g.dispose.length}
                      </Btn>
                    </span>
                  </td>
                )}
              </tr>
              {/* The document itself — the evidence the judgement is about:
                  every line under it renders here, the copies (and which one
                  Keep-one would keep) and the other lines that made it weak. */}
              {(g.doc_lines?.length ?? 0) > 0 && (
                <tr>
                  <td colSpan={7 + (showWhy ? 1 : 0) + (judge ? 1 : 0)}
                      className="pb-1.5 pl-6">
                    <div className="space-y-0.5">
                      {g.doc_lines!.map((l) => (
                        <p key={l.row_index}
                           className={`text-xs ${l.copy ? '' : 'text-muted-foreground'}`}>
                          row #{l.row_index} · {l.date} · ₹{fmtMoney(l.debit)}
                          {l.narration ? ` · ${l.narration}` : ''}
                          {' — '}
                          {l.copy ? (
                            l.row_index === g.keep ? (
                              <span className="font-semibold text-emerald-700">
                                identical copy (Keep one keeps this row)
                              </span>
                            ) : (
                              <span className="font-semibold text-orange-700">
                                identical copy (Keep one drops this row)
                              </span>
                            )
                          ) : (
                            'another line on this document'
                          )}
                        </p>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Keep one posting of each double-posted voucher, drop the extras. */
export function KeepOnePanel({ uploadId }: { uploadId: string }) {
  const qc = useQueryClient()
  // collapsed by default: summary + three actions; the detail tables render
  // only behind "Check them"
  const [checking, setChecking] = useState(false)
  const planQ = useQuery({
    queryKey: ['keep-one', uploadId],
    queryFn: () => api<KeepOnePlan>(`/uploads/${uploadId}/duplicates/keep-one/preview`),
  })
  const plan = planQ.data

  const invalidate = (extra: string[] = []) => {
    for (const k of ['keep-one', 'upload-rows', 'upload-companies', 'raw-summary', ...extra]) {
      void qc.invalidateQueries({ queryKey: [k] })
    }
  }

  const apply = useMutation({
    mutationFn: () =>
      postJson<{ groups: number; disposed: number; value: string; pending_review: boolean }>(
        `/uploads/${uploadId}/duplicates/keep-one`,
        // the count the preview showed: an approval is for the batch that was
        // described, not for whatever it has become since
        { expect_groups: plan?.takeable.groups },
      ),
    onSuccess: (r) => {
      showToast({
        color: 'green',
        title: `${r.groups} voucher(s) kept once`,
        message:
          `${r.disposed} duplicate posting(s) dropped, ₹${fmtMoney(r.value)}. `
          + (r.pending_review
            ? 'Recorded and awaiting a checker — nothing has moved yet.'
            : 'Each one is reversible from the Cleared view.'),
      })
      invalidate(['grid', 'name-mapping'])
    },
    onError: (e) =>
      showToast({
        color: 'red', title: 'Could not apply that',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  // The two judgements a held-back group can receive, posted through the same
  // row-grain door the dup? menu uses. Keep-one needs two payloads (kept row
  // real, extras genuine); they go in order, and a failure surfaces rather
  // than half-recording silently.
  const judgeWeak = useMutation({
    mutationFn: async (p: { g: KeepOneGroup; decision: 'both_real' | 'keep_one' }) => {
      for (const body of weakVerdicts(p.g, p.decision)) {
        await postJson(`/uploads/${uploadId}/duplicates/resolve`, body)
      }
      return p
    },
    onSuccess: (p) => {
      showToast({
        color: 'green',
        title: 'Judgement recorded',
        message: p.decision === 'both_real'
          ? `${p.g.docno || p.g.payor}: all ${p.g.n} lines judged real — nothing is dropped.`
          : `${p.g.docno || p.g.payor}: one kept, ${p.g.dispose.length} judged duplicate. `
            + 'Reversible from the Cleared view once approved.',
      })
      invalidate(['raw-dup-review', 'grid', 'name-mapping'])
    },
    onError: (e) =>
      showToast({
        color: 'red', title: 'Could not record that judgement',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  const bothReal = useMutation({
    mutationFn: (undecided: number) =>
      postJson<{ groups: number; pending_review: boolean }>(
        `/uploads/${uploadId}/duplicates/keep-one`,
        { decision: 'both_real', expect_groups: undecided },
      ),
    onSuccess: (r) => {
      showToast({
        color: 'green',
        title: `${r.groups} group(s) judged both real`,
        message: 'Nothing is dropped — every copy stays booked.'
          + (r.pending_review ? ' Recorded and awaiting a checker.' : ''),
      })
      invalidate(['raw-dup-review', 'grid', 'name-mapping'])
    },
    onError: (e) =>
      showToast({
        color: 'red', title: 'Could not record that',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  if (planQ.isLoading) return <p className="text-xs text-muted-foreground">Checking vouchers…</p>
  if (!plan || !plan.reviewable || plan.groups === 0) return null

  // Everything decided: the surface's work is done, so it collapses to one
  // line — the decided chip keeps the state reachable, nothing else remains
  if (plan.recorded.groups >= plan.groups) {
    const chipInfo = decidedChip(plan.recorded)
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Copy size={16} /> Vouchers posted more than once — all decided
        {chipInfo && <Chip tone={chipInfo.color as Tone}>{chipInfo.label}</Chip>}
      </p>
    )
  }

  const decided = decidedChip(plan.recorded)

  return (
    <div className="rounded-md border border-border p-3">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Copy size={18} />
          <span className="text-sm font-semibold">Vouchers posted more than once</span>
          <Chip>{fmt.format(plan.groups)} group(s)</Chip>
          {/* What has already been answered — without this the panel went on
              offering groups that had just been decided. */}
          {decided && (
            <Chip
              tone={decided.color as Tone}
              title={
                `${fmt.format(plan.recorded.groups)} group(s) already decided — `
                + `${fmt.format(plan.recorded.rows_disposed)} line(s), ₹${fmtMoney(plan.recorded.value)}. `
                + (plan.recorded.awaiting_checker > 0
                  ? `${fmt.format(plan.recorded.awaiting_checker)} awaiting a checker — nothing has moved yet.`
                  : 'Reverse any of them from the Cleared view.')
              }
            >
              {decided.label}
            </Chip>
          )}
          <InfoTip>
            A voucher posted twice inflates the books by its own amount. One of the copies
            is the real expense, so the disposition is to <b>keep one and drop the rest</b> —
            marking the whole group a duplicate would dispose of the real posting too.
            Nothing is deleted from the file, and every group is reversible from the Cleared
            view afterwards.
          </InfoTip>
        </div>

        <div className="flex flex-wrap gap-2">
          <Btn size="xs" loading={bothReal.isPending}
            onClick={() => bothReal.mutate(plan.groups - plan.recorded.groups)}>
            Accept all {fmt.format(plan.groups - plan.recorded.groups)} (both real — drops nothing)
          </Btn>
          {plan.takeable.groups > 0 && (
            <Btn size="xs" kind="primary" loading={apply.isPending}
              onClick={() => apply.mutate()}>
              Keep one of each — drop {fmt.format(plan.takeable.rows_disposed)} line(s),
              ₹{fmtMoney(plan.takeable.value)}
            </Btn>
          )}
          <Btn size="xs" onClick={() => setChecking((v) => !v)}>
            {checking ? 'Hide the groups' : 'Check them'}
          </Btn>
        </div>

        {checking && plan.takeable.groups > 0 && (
          <>
            <p className="flex items-center gap-1 text-sm">
              <span>
                <b>{fmt.format(plan.takeable.groups)}</b> group(s) can be decided together:{' '}
                {plan.rule}.
              </span>
              <InfoTip w={420}>
                Shown below are the batch&apos;s edges, not its first few rows — the head of
                a money-ordered list is its easiest cases, and a sample of easy cases buys
                confidence the batch has not earned. These figures describe <b>this
                file</b>: a reconciliation only ever drops copies it actually carries as
                open entries, so the effect on any one run is at most this and can be less.
              </InfoTip>
            </p>
            <GroupTable rows={plan.sample} showWhy />
          </>
        )}

        {checking && plan.held_back.groups > 0 && (
          <>
            <p className="flex items-center gap-1 text-sm font-semibold text-orange-800">
              <AlertTriangle size={16} />
              {fmt.format(plan.held_back.groups)} group(s) to judge one by one — ₹
              {fmtMoney(plan.held_back.value)}
              <InfoTip w={400}>
                These are <b>not</b> in the batch: {plan.held_back.reason}. Judge each one
                here — <b>Both real</b> records that nothing is dropped; <b>Keep one</b>{' '}
                drops the extra copies like the batch would.
              </InfoTip>
            </p>
            <GroupTable rows={plan.held_back.items}
              judge={(g, decision) => judgeWeak.mutate({ g, decision })}
              busy={judgeWeak.isPending} />
          </>
        )}
      </div>
    </div>
  )
}
